import { createEffect, createEvent, createStore, sample } from 'effector';
import { closePositionFx, type Position } from '../../entities/position';
import { toastShown } from '../../entities/toast';
import { api, ApiError, unwrap, type Schemas } from '../../shared/api/client';
import { i18n } from '../../shared/lib/i18n';
import { latest } from '../../shared/lib/latest';

export const closeDialogOpened = createEvent<Position>();
export const closeDialogClosed = createEvent();

export const $position = createStore<Position | null>(null)
  .on(closeDialogOpened, (_, position) => position)
  .reset(closeDialogClosed, closePositionFx.done);

export const previewCloseFx = createEffect(({ id, price }: { id: number; price: string }) =>
  unwrap(
    api.POST('/api/v1/positions/{position_id}/preview-close', {
      params: { path: { position_id: id } },
      body: { price },
    }),
  ),
);

/** Call this, not previewCloseFx: only the latest input's answer is shown. */
export const closePreview = latest(previewCloseFx, { reset: closeDialogOpened });

export const $preview = createStore<Schemas['RealizedOut'] | null>(null)
  .on(closePreview.done, (_, result) => result)
  .reset(closeDialogOpened);

export const $errors = createStore<Record<string, string>>({})
  .on(closePositionFx.failData, (_, error) => (error instanceof ApiError ? error.fields : {}))
  .reset(closeDialogOpened, closePositionFx.done);

sample({
  clock: closePositionFx.doneData,
  fn: (position) => ({
    message: i18n.t('closePosition.closed', { symbol: position.instrument.display_symbol }),
    tone: 'neutral' as const,
  }),
  target: toastShown,
});

sample({
  clock: closePositionFx.failData,
  filter: (error) => !(error instanceof ApiError) || Object.keys(error.fields).length === 0,
  fn: (error) => ({ message: error.message, tone: 'error' as const }),
  target: toastShown,
});
